import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Modal } from '../shared/Modal';
import { Button } from '../shared/Button';
import { Spinner } from '../shared/Spinner';
import { Icon } from '../shared/Icons';
import { Stepper, type StepperStep } from '../shared/Stepper';
import { notifySuccess, notifyError } from '../../lib/toast';
import { useExtractRules, useBulkCreateRuleArticles } from '../../hooks/use-rules-admin';
import {
  RuleArticleFields,
  EMPTY_RULE,
  fromDraft,
  toInput,
  isRuleValid,
  type EditableRule,
} from './RuleArticleFields';

// Cap the PDF client-side (well under the API's 32 MB) so an oversized upload fails fast with a
// friendly message rather than a slow round-trip.
const MAX_PDF_BYTES = 20 * 1024 * 1024;

// Strip the `data:...;base64,` prefix — the API forwards the bare base64 straight to Claude.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result);
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function stepLabels(t: TFunction): string[] {
  return [
    t('admin.rulebooks.wizard.stepUpload'),
    t('admin.rulebooks.wizard.stepExtract'),
    t('admin.rulebooks.wizard.stepReview'),
    t('admin.rulebooks.wizard.stepConfirm'),
  ];
}

// Cosmetic phases shown while the single extract request runs — makes the 30s–2min wait feel like
// progress rather than a dead spinner.
function extractPhases(t: TFunction): string[] {
  return [
    t('admin.rulebooks.wizard.extractPhase1'),
    t('admin.rulebooks.wizard.extractPhase2'),
    t('admin.rulebooks.wizard.extractPhase3'),
  ];
}

interface AIWizardDialogProps {
  slug: string;
  sportName: string;
  onClose: () => void;
}

// Upload a rule-book PDF → Claude extracts draft articles → review/edit → confirm → bulk-create.
export function AIWizardDialog({ slug, sportName, onClose }: AIWizardDialogProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1); // 1 Upload · 2 Extract · 3 Review · 4 Confirm
  const [file, setFile] = useState<File | null>(null);
  const [drafts, setDrafts] = useState<EditableRule[]>([]);

  const extract = useExtractRules(slug);
  const bulkCreate = useBulkCreateRuleArticles(slug);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const stepperSteps: StepperStep[] = stepLabels(t).map((label, i) => ({
    number: i + 1,
    label,
    state: step === i + 1 ? 'active' : step > i + 1 ? 'completed' : 'future',
  }));

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0] ?? null;
    if (picked && picked.size > MAX_PDF_BYTES) {
      notifyError(t('admin.rulebooks.wizard.pdfTooLarge'));
      return;
    }
    setFile(picked);
  }

  // Kick off extraction: read the file → base64 → call the API. Advances to the animated
  // extracting view immediately, then to review on success (or back to upload on failure).
  async function runExtraction() {
    if (!file) return;
    setStep(2);
    try {
      const base64 = await fileToBase64(file);
      const articles = await extract.mutateAsync(base64);
      if (articles.length === 0) {
        notifyError(t('admin.rulebooks.wizard.noRulesFound'));
        setStep(1);
        return;
      }
      setDrafts(articles.map(fromDraft));
      setStep(3);
    } catch (err) {
      notifyError(err, t('admin.rulebooks.wizard.extractErrorToast'));
      setStep(1);
    }
  }

  async function confirmCreate() {
    const valid = drafts.filter(isRuleValid);
    if (valid.length === 0) {
      notifyError(t('admin.rulebooks.wizard.addAtLeastOneError'));
      return;
    }
    try {
      const created = await bulkCreate.mutateAsync(valid.map(toInput));
      notifySuccess(t('admin.rulebooks.wizard.rulesAddedToast', { count: created.length, sportName }));
      onClose();
    } catch (err) {
      // A 409 (RULE_EXISTS) names the offending number so the admin can fix it in Review.
      notifyError(err, t('admin.rulebooks.wizard.createErrorToast'));
    }
  }

  function updateDraft(index: number, next: EditableRule) {
    setDrafts((prev) => prev.map((d, i) => (i === index ? next : d)));
  }
  function removeDraft(index: number) {
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  }

  const footer = (
    <WizardFooter
      step={step}
      canExtract={Boolean(file)}
      canContinue={drafts.some(isRuleValid)}
      creating={bulkCreate.isPending}
      draftCount={drafts.filter(isRuleValid).length}
      onCancel={onClose}
      onExtract={runExtraction}
      onBack={() => setStep((s) => Math.max(1, s === 3 ? 1 : s - 1))}
      onContinue={() => setStep(4)}
      onCreate={confirmCreate}
    />
  );

  return (
    <Modal
      open
      onClose={onClose}
      title={t('admin.rulebooks.wizard.title')}
      sub={t('admin.rulebooks.wizard.sub', { sportName })}
      panelClass="max-w-3xl"
      footer={step === 2 ? undefined : footer}
    >
      <Stepper steps={stepperSteps} className="mb-5" />

      {step === 1 && (
        <UploadStep file={file} inputRef={fileInputRef} onPick={onPickFile} />
      )}
      {step === 2 && <ExtractingStep />}
      {step === 3 && (
        <ReviewStep drafts={drafts} onUpdate={updateDraft} onRemove={removeDraft} onAdd={() => setDrafts((p) => [...p, { ...EMPTY_RULE }])} />
      )}
      {step === 4 && <ConfirmStep count={drafts.filter(isRuleValid).length} sportName={sportName} />}
    </Modal>
  );
}

// ── Step 1: upload ──────────────────────────────────────────────────────────────
function UploadStep({
  file,
  inputRef,
  onPick,
}: {
  file: File | null;
  inputRef: React.RefObject<HTMLInputElement>;
  onPick: (e: React.ChangeEvent<HTMLInputElement>) => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 py-6 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-accent-tint text-accent">
        <Icon.spark className="h-6 w-6" />
      </div>
      <div>
        <p className="text-sm font-medium text-ink">{t('admin.rulebooks.wizard.uploadHeading')}</p>
        <p className="mt-1 text-xs text-muted">
          {t('admin.rulebooks.wizard.uploadHint')}
        </p>
      </div>

      <input ref={inputRef} type="file" accept="application/pdf" onChange={onPick} className="hidden" />
      <Button variant="default" size="md" onClick={() => inputRef.current?.click()}>
        <Icon.upload className="h-4 w-4" />
        {file ? t('admin.rulebooks.wizard.chooseDifferentPdf') : t('admin.rulebooks.wizard.choosePdf')}
      </Button>

      {file && (
        <div className="flex items-center gap-2 rounded-sm border border-line-2 bg-surface-2 px-3 py-2 text-[13px] text-ink">
          <Icon.doc className="h-4 w-4 flex-none text-muted" />
          <span className="truncate">{file.name}</span>
          <span className="flex-none text-muted">({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
        </div>
      )}
    </div>
  );
}

// ── Step 2: extracting (animated) ─────────────────────────────────────────────────
function ExtractingStep() {
  const { t } = useTranslation();
  const phases = extractPhases(t);
  const [phase, setPhase] = useState(0);
  // Cycle the phase labels while the request runs — purely cosmetic progress.
  useEffect(() => {
    const id = setInterval(() => setPhase((p) => (p + 1) % phases.length), 1600);
    return () => clearInterval(id);
  }, [phases.length]);

  return (
    <div className="flex flex-col items-center gap-5 py-12 text-center">
      <div className="relative grid h-16 w-16 place-items-center">
        <span className="absolute inset-0 animate-ping rounded-full bg-accent/20" />
        <span className="grid h-16 w-16 place-items-center rounded-full bg-accent-tint text-accent">
          <Icon.spark className="h-7 w-7" />
        </span>
      </div>
      <div className="flex items-center gap-2 text-sm font-medium text-ink">
        <Spinner className="h-4 w-4 text-accent" />
        <span>{phases[phase]}</span>
      </div>
      {/* Indeterminate accent bar — pulses so the wait reads as active work. */}
      <div className="h-1 w-56 overflow-hidden rounded-full bg-surface-3">
        <div className="h-full w-1/2 animate-pulse rounded-full bg-accent" />
      </div>
      <p className="max-w-sm text-xs text-muted">
        {t('admin.rulebooks.wizard.extractWaitNote')}
      </p>
    </div>
  );
}

// ── Step 3: review ────────────────────────────────────────────────────────────────
function ReviewStep({
  drafts,
  onUpdate,
  onRemove,
  onAdd,
}: {
  drafts: EditableRule[];
  onUpdate: (i: number, next: EditableRule) => void;
  onRemove: (i: number) => void;
  onAdd: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-ink-2">
          <Trans
            i18nKey="admin.rulebooks.wizard.reviewCount"
            count={drafts.length}
            values={{ count: drafts.length }}
            components={{ bold: <span className="font-semibold text-ink" /> }}
          />
        </p>
        <Button variant="ghost" size="sm" onClick={onAdd}>
          <Icon.plus className="h-3.5 w-3.5" />
          {t('admin.rulebooks.addRule')}
        </Button>
      </div>

      {drafts.map((draft, i) => (
        <div
          key={i}
          // Staggered fade+rise so the extracted rules "land" one after another.
          className="animate-in fade-in slide-in-from-bottom-2 rounded border border-line-2 bg-surface p-3 shadow-1"
          style={{ animationDelay: `${Math.min(i, 8) * 55}ms`, animationFillMode: 'both' }}
        >
          <div className="mb-2 flex items-center justify-between">
            <span className="text-[11px] font-medium uppercase tracking-wider text-muted">
              {t('admin.rulebooks.wizard.ruleNumberFallback', { number: draft.article_number || i + 1 })}
            </span>
            <button
              type="button"
              onClick={() => onRemove(i)}
              aria-label={t('admin.rulebooks.wizard.removeThisRuleAria')}
              className="grid h-6 w-6 place-items-center rounded-sm text-muted transition-colors hover:bg-danger-tint hover:text-danger"
            >
              <Icon.trash className="h-3.5 w-3.5" />
            </button>
          </div>
          <RuleArticleFields value={draft} onChange={(next) => onUpdate(i, next)} idPrefix={`draft-${i}`} />
        </div>
      ))}

      {drafts.length === 0 && (
        <div className="rounded border border-dashed border-line-2 bg-bg-2 px-4 py-8 text-center text-sm text-muted">
          {t('admin.rulebooks.wizard.allRemovedNote')}
        </div>
      )}
    </div>
  );
}

// ── Step 4: confirm ─────────────────────────────────────────────────────────────
function ConfirmStep({ count, sportName }: { count: number; sportName: string }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-4 py-8 text-center">
      <div className="grid h-14 w-14 place-items-center rounded-full bg-success-tint text-success">
        <Icon.check className="h-6 w-6" />
      </div>
      <div>
        <p className="text-sm font-medium text-ink">
          {t('admin.rulebooks.wizard.confirmQuestion', { count, sportName })}
        </p>
        <p className="mt-1 text-xs text-muted">
          {t('admin.rulebooks.wizard.confirmNote')}
        </p>
      </div>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────────
function WizardFooter({
  step,
  canExtract,
  canContinue,
  creating,
  draftCount,
  onCancel,
  onExtract,
  onBack,
  onContinue,
  onCreate,
}: {
  step: number;
  canExtract: boolean;
  canContinue: boolean;
  creating: boolean;
  draftCount: number;
  onCancel: () => void;
  onExtract: () => void;
  onBack: () => void;
  onContinue: () => void;
  onCreate: () => void;
}) {
  const { t } = useTranslation();
  if (step === 1) {
    return (
      <>
        <Button variant="default" size="md" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button variant="accent" size="md" onClick={onExtract} disabled={!canExtract}>
          <Icon.spark className="h-4 w-4" />
          {t('admin.rulebooks.wizard.extractRules')}
        </Button>
      </>
    );
  }
  if (step === 3) {
    return (
      <>
        <Button variant="default" size="md" onClick={onBack}>
          {t('admin.rulebooks.wizard.startOver')}
        </Button>
        <Button variant="accent" size="md" onClick={onContinue} disabled={!canContinue}>
          {t('admin.rulebooks.wizard.continueButton')}
        </Button>
      </>
    );
  }
  // step 4
  return (
    <>
      <Button variant="default" size="md" onClick={onBack} disabled={creating}>
        {t('admin.rulebooks.wizard.back')}
      </Button>
      <Button variant="accent" size="md" onClick={onCreate} disabled={creating}>
        {creating ? <Spinner className="h-4 w-4" /> : t('admin.rulebooks.wizard.createButton', { count: draftCount })}
      </Button>
    </>
  );
}
